import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { FilterType } from 'src/app/shared/models/constants/filter-type';
import { Coupon } from 'src/app/shared/models/dtos/responses/coupon/coupon';

@Component({
  selector: 'app-coupons-list',
  templateUrl: './coupons-list.component.html',
  styleUrls: ['./coupons-list.component.scss'],
})
/**
 * Display main components of coupons page
 */
export class CouponsListComponent implements OnInit, OnDestroy {
  @Input() couponsList: Coupon[] = [];
  couponsVip: any;
  labels = {
    coupons: 'coupons',
    notFound: 'coupon-not-listed',
    notFoundDescription: 'coupon-not-listed-description',
    rewardCoupons: 'reward-coupons',
    vipCoupon: 'vip-coupon',
  };
  loading = false;
  pulling = false;
  private page = 0;
  private pageSize = 25;
  private subsink = new Subscription();
  private apiCall?: Subscription;

  private activeChip = new ChipContent(
    undefined,
    'active',
    FilterType.COUPONS_ACTIVE,
    false
  );

  private redeemedChip = new ChipContent(
    undefined,
    'redeemed',
    FilterType.COUPONS_REDEEMED,
    false
  );

  private canceledChip = new ChipContent(
    undefined,
    'canceled',
    FilterType.COUPONS_CANCELED,
    false
  );

  private expiredChip = new ChipContent(
    undefined,
    'expired',
    FilterType.COUPONS_EXPIRED,
    false
  );

  chipContents = [
    this.activeChip,
    this.redeemedChip,
    this.canceledChip,
    this.expiredChip,
  ];

  filters = {
    active: false,
    used: false,
    canceled: false,
    expired: false,
  };

  /**
   * @constructor
   */
  constructor(
    private translationService: TranslationService,
    public rewardService: RewardService,
    public userService: UserService
  ) {}

  /**
   * on init
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.labels);
    this.pullCoupons();
    this.pullCouponVip();
  }

  /**
   * cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Loads more items
   */
  pullCoupons() {
    this.apiCall?.unsubscribe();
    if (this.couponsList.length > 0) {
      this.pulling = true;
    } else {
      this.loading = true;
    }
    const params = {
      page: this.page,
      pageSize: this.pageSize,
    };
    this.assignParams(params);
    const coupons$ = this.rewardService
      .getCoupons(params)
      .subscribe((coupons) => {
        if (coupons.length > 0) {
          this.page++;
        }
        if (this.couponsList.length > 0) {
          this.pulling = false;
        } else {
          this.loading = false;
        }
        this.couponsList.push(...coupons);
      });
    this.apiCall = coupons$;
    this.subsink.add(coupons$);
  }

  /**
   * Pulls vip coupon
   */
  pullCouponVip() {
    const couponsVip$ = this.userService
      .getCouponsVip()
      .subscribe((coupons) => {
        this.couponsVip = coupons;
      });
    this.subsink.add(couponsVip$);
  }

  /**
   * Generate filter params and emit it
   * @param {ChipContent} element
   */
  getFilter(element: ChipContent) {
    if (element.filterType === FilterType.COUPONS_ACTIVE) {
      element.isActive = !element.isActive;
      this.filters.active = !this.filters.active;
      this.resetList();
      this.pullCoupons();
    }

    if (element.filterType === FilterType.COUPONS_REDEEMED) {
      element.isActive = !element.isActive;
      this.filters.used = !this.filters.used;
      this.resetList();
      this.pullCoupons();
    }

    if (element.filterType === FilterType.COUPONS_CANCELED) {
      element.isActive = !element.isActive;
      this.filters.canceled = !this.filters.canceled;
      this.resetList();
      this.pullCoupons();
    }

    if (element.filterType === FilterType.COUPONS_EXPIRED) {
      element.isActive = !element.isActive;
      this.filters.expired = !this.filters.expired;
      this.resetList();
      this.pullCoupons();
    }

    this.filters = { ...this.filters };
  }

  /**
   * Assign filter params to be fetched
   * @param {{}} params
   */
  private assignParams(params: {}) {
    if (
      this.filters.active ||
      this.filters.used ||
      this.filters.canceled ||
      this.filters.expired
    ) {
      Object.assign(params, this.filters);
    }
  }

  /**
   * Resets coupons list
   */
  private resetList() {
    this.couponsList = [];
    this.page = 0;
  }
}
